// src/pages/ProfilePage.js — Hub central v6
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";
import { useAuth } from "../hooks/useAuth";
import { BADGES_LIST, computeLevel, getWeeklyHoroscope, getOniMatchups, getAvoidReason, getTrainReason, getOniTip, ONI_FORMS, STAT_META, ONI_IMG_VERSION } from "../data/demons";
import OniCard from "../components/OniCard";
import "./ProfilePage.css";

// ── Mentor Section — demande conseil à un autre Oni ─────────────────────
function MentorSection({ myOniId }) {
  const others = ONI_FORMS.filter(o => o.id !== myOniId);
  const [selectedId, setSelectedId] = useState(others[0]?.id || null);
  const selected = ONI_FORMS.find(o => o.id === selectedId);
  const tipData  = selectedId ? getOniTip(selectedId) : null;

  return (
    <div className="pp-mentor-section">
      <p className="pp-block-label" style={{marginBottom:"0.5rem"}}>
        🧠 DEMANDER CONSEIL À UN AUTRE ONI
      </p>
      <p className="pp-matchup-sub" style={{marginBottom:"1rem"}}>
        Chaque Oni maîtrise un domaine. Choisis-en un pour recevoir son conseil de la semaine.
      </p>

      <div className="pp-mentor-selector">
        {others.map(o => (
          <button
            key={o.id}
            className={`pp-mentor-btn${selectedId === o.id ? " active" : ""}`}
            onClick={() => setSelectedId(o.id)}
            title={o.name}
          >
            {o.image ? (
              <img src={`${o.image}?v=${ONI_IMG_VERSION}`} alt={o.name} className="pp-mentor-img"/>
            ) : (
              <span>{o.emoji}</span>
            )}
          </button>
        ))}
      </div>

      {selected && tipData && (
        <div className="pp-mentor-card">
          {selected.image ? (
            <img src={`${selected.image}?v=${ONI_IMG_VERSION}`} alt={selected.name} className="pp-mentor-card-img"/>
          ) : (
            <span className="pp-mentor-card-emoji">{selected.emoji}</span>
          )}
          <div className="pp-mentor-card-info">
            <p className="pp-mentor-card-name">{selected.jp} · {selected.name}</p>
            <p className="pp-mentor-card-domain">{tipData.domain}</p>
            <p className="pp-mentor-card-tip">"{tipData.tip}"</p>
          </div>
        </div>
      )}
    </div>
  );
}

function StatMini({ statKey, value }) {
  const meta = STAT_META[statKey];
  return (
    <div className="pp-stat-row">
      <span className="pp-stat-label">{meta?.label || statKey}</span>
      <div className="pp-stat-track">
        <div className="pp-stat-fill" style={{ width: value + "%", background: meta?.color || "var(--blood)" }} />
      </div>
      <span className="pp-stat-val" style={{ color: meta?.color }}>{value}</span>
    </div>
  );
}

function XpBar({ xp, levelInfo }) {
  return (
    <div className="pp-xp-wrap">
      <div className="pp-xp-labels">
        <span className="pp-xp-current">{xp} XP</span>
        {levelInfo.next && (
          <span className="pp-xp-next">→ {levelInfo.next.xp} XP · LV.{levelInfo.next.level}</span>
        )}
      </div>
      <div className="pp-xp-track">
        <div className="pp-xp-fill" style={{ width: levelInfo.progress + "%" }} />
      </div>
    </div>
  );
}

export default function ProfilePage() {
  const { user, profile, logout, refreshProfile } = useAuth();
  const navigate = useNavigate();

  const demon           = profile?.demon;
  const oni             = demon?.oni;
  const stats           = demon?.stats || {};
  const perks           = demon?.perks || [];
  const xp              = profile?.xp || 0;
  const badges          = profile?.badges || [];
  const levelInfo       = computeLevel(xp);
  const isPublic        = profile?.profilePublic !== false;
  const journalCount    = profile?.journal?.length || 0;
  const lastReform      = profile?.lastReformAt?.toDate?.();
  const daysSinceReform = lastReform
    ? Math.floor((Date.now() - lastReform.getTime()) / (1000 * 60 * 60 * 24)) : 999;
  const reformEligible  = journalCount >= 10 || daysSinceReform >= 30;
  const horoscope       = oni ? getWeeklyHoroscope(oni.id) : null;
  const matchups        = (oni && stats) ? getOniMatchups(stats, oni.id) : { avoid: [], train: [] };

  const topStats = Object.entries(stats).sort(([,a],[,b]) => b-a).slice(0,5);

  const orderScore = stats ? Math.round(((stats.control||0)+(stats.patience||0)+(stats.discipline||0))/3) : 0;
  const chaosScore = stats ? Math.round(((stats.chaos||0)+(stats.risk||0)+(stats.creativity||0))/3) : 0;
  const chaosPos   = orderScore + chaosScore > 0
    ? Math.round((chaosScore / (orderScore + chaosScore)) * 100) : 50;

  const [activeTab,      setActiveTab]      = useState("demon");
  const [togglingPublic, setTogglingPublic] = useState(false);
  const [copied,         setCopied]         = useState(false);
  const [loggingOut,     setLoggingOut]     = useState(false);
  const [quickNote,      setQuickNote]      = useState("");
  const [noteSaving,     setNoteSaving]     = useState(false);
  const [noteDone,       setNoteDone]       = useState(false);
  const [showOniCard,    setShowOniCard]    = useState(false);

  if (!user) return (
    <div className="profile-page section" style={{ paddingTop: "8rem", textAlign: "center" }}>
      <span className="section-eyebrow">鬼 · Profil</span>
      <h2 className="section-title">Tu n'es pas encore<br />dans le registre</h2>
      <div className="divider divider-center" />
      <Link to="/auth" className="btn btn-primary" style={{ marginTop: "1.5rem" }}>ENTRER →</Link>
    </div>
  );

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate("/");
  };

  const togglePublic = async () => {
    setTogglingPublic(true);
    await updateDoc(doc(db, "users", user.uid), { profilePublic: !isPublic });
    await refreshProfile();
    setTogglingPublic(false);
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/u/${user.uid}`)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const saveQuickNote = async () => {
    if (!quickNote.trim()) return;
    setNoteSaving(true);
    const entry = { text: quickNote.trim(), date: new Date().toISOString(), demonName: oni?.name || "Oni" };
    const existing = profile?.journal || [];
    await updateDoc(doc(db, "users", user.uid), {
      journal: [entry, ...existing].slice(0, 100),
      xp: xp + 25,
      lastJournalAt: serverTimestamp(),
    });
    await refreshProfile();
    setQuickNote(""); setNoteDone(true);
    setTimeout(() => setNoteDone(false), 2500);
    setNoteSaving(false);
  };

  const recentJournal = (profile?.journal || []).slice(0, 5);

  return (
    <>
      <div className="profile-page">
        <div className="section" style={{ paddingTop: "7rem" }}>

          {/* BLOC 1 — Identité */}
          <div className="pp-identity-block">
            <div className="pp-avatar-col">
              {oni?.image ? (
                <div className="pp-avatar-frame">
                  <img src={`${oni.image}?v=2`} alt={oni.name} className="pp-avatar-img" />
                  {reformEligible && <div className="pp-avatar-reform-dot" title="Oni Reform disponible"/>}
                </div>
              ) : (
                <div className="pp-avatar-frame pp-avatar-emoji">
                  <span>{oni?.emoji || "👹"}</span>
                  {reformEligible && <div className="pp-avatar-reform-dot"/>}
                </div>
              )}
              <div className="pp-level-pill">
                <span>{levelInfo.current.badge}</span>
                <span>LV.{levelInfo.current.level}</span>
              </div>
            </div>

            <div className="pp-info-col">
              <p className="pp-eyebrow">鬼 · PROFIL DÉMONIAQUE</p>
              <h1 className="pp-player-name">{profile?.displayName || user.displayName || "Oni Inconnu"}</h1>
              {oni && (
                <div className="pp-oni-line">
                  <span className="pp-oni-jp">{oni.jp}</span>
                  <span className="pp-oni-sep">·</span>
                  <span className="pp-oni-en">{oni.name}</span>
                </div>
              )}
              {oni?.tagline && <p className="pp-oni-tagline">"{oni.tagline}"</p>}
              <XpBar xp={xp} levelInfo={levelInfo} />
              <div className="pp-meta-row">
                <span className="badge badge-ghost">{levelInfo.current.title}</span>
                <span className="badge badge-ghost">📜 {journalCount} chroniques</span>
                <span className="badge badge-ghost">⚔️ {profile?.weeklyDuelsWon || 0} duels</span>
              </div>
            </div>

            <div className="pp-top-actions">
              <div className="pp-visibility-toggle">
                <span className={`pp-vis-dot${isPublic ? " on" : ""}`}/>
                <span className="pp-vis-label">{isPublic ? "PUBLIC" : "PRIVÉ"}</span>
                {isPublic && (
                  <button className="pp-vis-btn" onClick={copyLink}>
                    {copied ? "✓" : "↗"}
                  </button>
                )}
                <button className="pp-vis-btn" onClick={togglePublic} disabled={togglingPublic}>
                  {togglingPublic ? "..." : isPublic ? "🔒" : "🌐"}
                </button>
              </div>
              <button className="pp-logout-btn" onClick={handleLogout} disabled={loggingOut}>
                {loggingOut ? <span className="spinner" style={{width:12,height:12}}/> : "↩ SORTIR"}
              </button>
            </div>
          </div>

          {/* BLOC 2 — Reform notice */}
          {reformEligible && (
            <div className="pp-reform-banner">
              <span className="pp-reform-flame">🔥</span>
              <div>
                <p className="pp-reform-title">Ton démon est prêt à se reformer</p>
                <p className="pp-reform-sub">
                  {journalCount >= 10 ? `${journalCount} sessions inscrites` : `${daysSinceReform} jours écoulés`}
                </p>
              </div>
              <div className="pp-reform-actions">
                <Link to="/reform" className="btn btn-primary btn-sm">🔥 REFORM</Link>
                <Link to="/evolve" className="btn btn-ghost btn-sm">⬆️ EVOLVE</Link>
              </div>
            </div>
          )}

          {!demon ? (
            <div className="pp-no-demon">
              <span>👹</span>
              <p>Ton démon n'est pas encore apparu.</p>
              <Link to="/awaken" className="btn btn-primary" style={{ marginTop: "1.5rem" }}>
                RÉVEILLER MON DÉMON →
              </Link>
            </div>
          ) : (
            <>
              {/* BLOC 3 — Tabs */}
              <div className="pp-tabs">
                {[
                  ["demon",     "👹 MON ONI"],
                  ["stats",     "📊 STATS"],
                  ["horoscope", "🔮 CONSEILS"],
                  ["badges",    "🎖️ BADGES"],
                  ["journal",   "💬 ONITALK"],
                ].map(([id, label]) => (
                  <button key={id}
                    className={`pp-tab${activeTab === id ? " active" : ""}`}
                    onClick={() => setActiveTab(id)}>
                    {label}
                  </button>
                ))}
              </div>

              <div className="pp-tab-content">

                {/* MON ONI */}
                {activeTab === "demon" && (
                  <div className="pp-tab-demon">
                    <div className="pp-demon-split">
                      <div className="pp-demon-portrait">
                        {oni?.image && (
                          <img src={`${oni.image}?v=2`} alt={oni.name} className="pp-demon-full-img"/>
                        )}
                        <div className="pp-demon-lore">
                          <p className="pp-demon-lore-jp">{oni.jp}</p>
                          <h2 className="pp-demon-lore-name">{oni.name}</h2>
                          <p className="pp-demon-lore-desc">{oni.desc}</p>
                          {perks.length > 0 && (
                            <div className="pp-perks">
                              <p className="pp-perks-label">PERKS</p>
                              <div className="pp-perks-list">
                                {perks.map(p => (
                                  <span key={p} className="pp-perk-tag">⚡ {p.replace(/_/g," ").toUpperCase()}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="pp-demon-actions-col">
                        <div className="pp-axis-block">
                          <div className="pp-axis-labels">
                            <span>⚖ ORDRE</span>
                            <span>CHAOS ☠</span>
                          </div>
                          <div className="pp-axis-track">
                            <div className="pp-axis-fill" style={{ width: chaosPos + "%" }}/>
                            <div className="pp-axis-pip" style={{ left: `calc(${chaosPos}% - 5px)` }}/>
                          </div>
                          <div className="pp-axis-vals">
                            <span style={{color:"var(--blue-oni)"}}>{orderScore}</span>
                            <span style={{color:"var(--blood)"}}>{chaosScore}</span>
                          </div>
                        </div>

                        <div className="pp-top-stats">
                          <p className="pp-block-label">STATS DOMINANTES</p>
                          {topStats.slice(0,3).map(([k,v]) => <StatMini key={k} statKey={k} value={v}/>)}
                        </div>

                        <div className="pp-cta-grid">
                          <Link to="/journal" className="pp-cta-card pp-cta-primary">
                            <span>💬</span><p>ONITALK</p><small>Parle à ton démon</small>
                          </Link>
                          <Link to="/duel" className="pp-cta-card">
                            <span>⚔️</span><p>DUEL</p><small>Affronter un Oni</small>
                          </Link>
                          <Link to="/coach" className="pp-cta-card">
                            <span>🎥</span><p>COACH</p><small>Analyse vidéo</small>
                          </Link>
                          <Link to="/fftt" className="pp-cta-card">
                            <span>🎯</span><p>PROIES</p><small>Chercher des cibles</small>
                          </Link>
                          <button className="pp-cta-card pp-cta-share" onClick={() => setShowOniCard(true)}>
                            <span>📸</span><p>CARTE ONI</p><small>Story Instagram</small>
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Horoscope hebdo — remonté sur l'onglet principal */}
                    {horoscope && (
                      <div className="pp-demon-horoscope">
                        <p className="pp-block-label" style={{marginBottom:"1rem"}}>
                          🔮 HOROSCOPE DE LA SEMAINE · {oni?.name?.toUpperCase()}
                        </p>
                        <div className="pp-horo-grid">
                          {horoscope.map((h, i) => (
                            <div key={i} className="pp-horo-card">
                              <div className="pp-horo-header">
                                <span className="pp-horo-intensity">{h.intensity}</span>
                                <span className="pp-horo-domain">{h.domain}</span>
                              </div>
                              <p className="pp-horo-text">{h.text}</p>
                            </div>
                          ))}
                        </div>
                        <button className="btn btn-ghost btn-sm" onClick={() => setActiveTab("horoscope")} style={{marginTop:"1rem"}}>
                          🔮 VOIR MES CONSEILS →
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* STATS */}
                {activeTab === "stats" && (
                  <div className="pp-tab-stats">
                    <div className="pp-stats-grid">
                      {Object.entries(stats).map(([k,v]) => <StatMini key={k} statKey={k} value={v}/>)}
                    </div>
                    <div className="pp-stats-axis">
                      <p className="pp-block-label">AXE ORDRE / CHAOS</p>
                      <div className="pp-axis-track" style={{height:10}}>
                        <div className="pp-axis-fill" style={{ width: chaosPos + "%" }}/>
                        <div className="pp-axis-pip" style={{ left: `calc(${chaosPos}% - 5px)` }}/>
                      </div>
                      <div className="pp-axis-vals">
                        <span style={{color:"var(--blue-oni)"}}>ORDRE {orderScore}</span>
                        <span style={{color:"var(--blood)"}}>CHAOS {chaosScore}</span>
                      </div>
                    </div>
                    <div className="pp-stats-footer">
                      <Link to="/evolve" className="btn btn-ghost btn-sm">FAIRE ÉVOLUER MES STATS ↗</Link>
                      <Link to="/journal" className="btn btn-primary btn-sm">💬 ONITALK →</Link>
                    </div>
                  </div>
                )}

                {/* CONSEILS */}
                {activeTab === "horoscope" && (
                  <div className="pp-tab-horoscope">
                    {/* Mentor — demander conseil à un autre Oni */}
                    <MentorSection myOniId={oni?.id}/>

                    {/* Matchups — adversaires à éviter en match */}
                    {matchups.avoid.length > 0 && (
                      <div className="pp-matchup-section">
                        <p className="pp-block-label pp-matchup-title pp-matchup-avoid">
                          ⚠️ ADVERSAIRES À ÉVITER EN MATCH
                        </p>
                        <p className="pp-matchup-sub">
                          Ces profils créent le plus de friction avec le tien — sois prêt mentalement.
                        </p>
                        <div className="pp-matchup-grid">
                          {matchups.avoid.map(({ oni: theirOni }) => (
                            <div key={theirOni.id} className="pp-matchup-card pp-matchup-avoid-card">
                              {theirOni.image ? (
                                <img src={`${theirOni.image}?v=${ONI_IMG_VERSION}`} alt={theirOni.name} className="pp-matchup-img"/>
                              ) : (
                                <span className="pp-matchup-emoji">{theirOni.emoji}</span>
                              )}
                              <div className="pp-matchup-info">
                                <p className="pp-matchup-name">{theirOni.jp} · {theirOni.name}</p>
                                <p className="pp-matchup-reason">{getAvoidReason(oni.id, theirOni)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Matchups — partenaires d'entraînement recommandés */}
                    {matchups.train.length > 0 && (
                      <div className="pp-matchup-section">
                        <p className="pp-block-label pp-matchup-title pp-matchup-train">
                          🎯 PARTENAIRES D'ENTRAÎNEMENT RECOMMANDÉS
                        </p>
                        <p className="pp-matchup-sub">
                          Affronter ces styles régulièrement renforce tes points faibles sans te déstabiliser.
                        </p>
                        <div className="pp-matchup-grid">
                          {matchups.train.map(({ oni: theirOni }) => (
                            <div key={theirOni.id} className="pp-matchup-card pp-matchup-train-card">
                              {theirOni.image ? (
                                <img src={`${theirOni.image}?v=${ONI_IMG_VERSION}`} alt={theirOni.name} className="pp-matchup-img"/>
                              ) : (
                                <span className="pp-matchup-emoji">{theirOni.emoji}</span>
                              )}
                              <div className="pp-matchup-info">
                                <p className="pp-matchup-name">{theirOni.jp} · {theirOni.name}</p>
                                <p className="pp-matchup-reason">{getTrainReason(oni.id, theirOni)}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <Link to="/lore" className="btn btn-ghost btn-sm" style={{marginTop:"1.5rem"}}>
                      DÉCOUVRIR TOUS LES ONI →
                    </Link>
                  </div>
                )}

                {/* BADGES */}
                {activeTab === "badges" && (
                  <div className="pp-tab-badges">
                    <div className="pp-badges-grid">
                      {BADGES_LIST.map(b => {
                        const unlocked = badges.includes(b.id);
                        return (
                          <div key={b.id} className={`pp-badge-card${unlocked ? " unlocked" : " locked"}`}>
                            <span className="pp-badge-icon">{b.icon}</span>
                            <p className="pp-badge-label">{b.label}</p>
                            <p className="pp-badge-desc">{b.desc}</p>
                            <p className="pp-badge-xp">+{b.xp} XP</p>
                            {!unlocked && <div className="pp-badge-lock">🔒</div>}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* JOURNAL */}
                {activeTab === "journal" && (
                  <div className="pp-tab-journal">
                    <div className="pp-quick-note card">
                      <p className="pp-block-label">NOTE RAPIDE · +25 XP</p>
                      <p style={{fontSize:".72rem",color:"var(--ash)",marginBottom:".75rem",fontStyle:"italic"}}>
                        Pour les chroniques IA, va sur{" "}
                        <Link to="/journal" style={{color:"var(--blood)"}}>le Journal</Link>.
                      </p>
                      <textarea className="pp-quick-input" value={quickNote}
                        onChange={e => setQuickNote(e.target.value)}
                        placeholder="Ce qui s'est passé en session..." rows={3}/>
                      <button className="btn btn-primary btn-sm"
                        style={{width:"100%",justifyContent:"center",marginTop:"8px"}}
                        onClick={saveQuickNote} disabled={noteSaving||!quickNote.trim()}>
                        {noteSaving ? <><span className="spinner"/>SAUVEGARDE...</>
                          : noteDone ? "✓ NOTÉ +25 XP"
                          : "NOTER"}
                      </button>
                    </div>

                    <div className="pp-journal-recent">
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"1rem"}}>
                        <p className="pp-block-label">DERNIÈRES ENTRÉES · {journalCount}</p>
                        <Link to="/journal" className="btn btn-ghost btn-sm">TOUT VOIR →</Link>
                      </div>
                      {recentJournal.length === 0 ? (
                        <p style={{fontSize:".78rem",color:"var(--ash)",fontStyle:"italic"}}>
                          Aucune entrée. Commence à noter tes sessions.
                        </p>
                      ) : recentJournal.map((e, i) => (
                        <div key={i} className="pp-journal-row">
                          <div className="pp-journal-row-meta">
                            <span>{e.demonName || oni?.name}</span>
                            <span>{new Date(e.date).toLocaleDateString("fr-FR",{day:"numeric",month:"short"})}</span>
                          </div>
                          <p className="pp-journal-row-text">{e.title || e.text || e.chronicle}</p>
                          {e.verdict && <span className="pp-journal-verdict">{e.verdict}</span>}
                        </div>
                      ))}
                      {journalCount > 5 && (
                        <Link to="/journal" className="pp-journal-more-link">
                          +{journalCount-5} chroniques dans le journal complet →
                        </Link>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* OniCard modal — en dehors du flux principal */}
      {showOniCard && demon && (
        <OniCard
          demon={demon}
          xp={xp}
          displayName={profile?.displayName || user.displayName}
          onClose={() => setShowOniCard(false)}
        />
      )}
    </>
  );
}
